import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import { buildPlaylistUrl } from "./playlistUrl";

// --- Live streaming hooks ---

export const useLiveAngleVideoUrl = (
  location: string | null,
  angle: string | null,
  {
    segmentCount = 8,
    deviceId,
  }: { segmentCount?: number; deviceId?: number } = {}
) => {
  const tenantName = useTenant();

  const url = useMemo(() => {
    if (location == null || angle == null) return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/video/${location}/${angle}/live.m3u8`,
      {
        segment_count: String(segmentCount),
        ...(deviceId != null ? { device_id: String(deviceId) } : {}),
      }
    );
  }, [tenantName, location, angle, segmentCount, deviceId]);

  return {
    url,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: undefined,
  };
};

export const useLiveCameraVideoUrl = (
  deviceId: number | null,
  physicalId: string | null,
  { segmentCount = 8 }: { segmentCount?: number } = {}
) => {
  const tenantName = useTenant();

  const url = useMemo(() => {
    if (deviceId == null || physicalId == null) return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/video/device/${deviceId}/camera/${physicalId}/live.m3u8`,
      { segment_count: String(segmentCount) }
    );
  }, [tenantName, deviceId, physicalId, segmentCount]);

  return {
    url,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: undefined,
  };
};

// --- Historical/VOD hooks ---

export const useHistoricalAngleVideoUrl = (
  location: string | null,
  angle: string | null,
  startTime: string | null,
  endTime: string | null,
  deviceId?: number
) => {
  const tenantName = useTenant();

  const url = useMemo(() => {
    if (
      location == null ||
      angle == null ||
      startTime == null ||
      endTime == null ||
      startTime >= endTime
    )
      return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/video/${location}/${angle}/playlist.m3u8`,
      {
        start_time: startTime,
        end_time: endTime,
        ...(deviceId != null ? { device_id: String(deviceId) } : {}),
      }
    );
  }, [tenantName, location, angle, startTime, endTime, deviceId]);

  return {
    url,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: undefined,
  };
};

export const useHistoricalCameraVideoUrl = (
  deviceId: number | null,
  physicalId: string | null,
  startTime: string | null,
  endTime: string | null
) => {
  const tenantName = useTenant();

  const url = useMemo(() => {
    if (
      deviceId == null ||
      physicalId == null ||
      startTime == null ||
      endTime == null ||
      startTime >= endTime
    )
      return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/video/device/${deviceId}/camera/${physicalId}/playlist.m3u8`,
      { start_time: startTime, end_time: endTime }
    );
  }, [tenantName, deviceId, physicalId, startTime, endTime]);

  return {
    url,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: undefined,
  };
};
